import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  LinearProgress,
  Typography,
} from "@mui/material";
import UploadFile from "@mui/icons-material/UploadFile";
import FileDownload from "@mui/icons-material/FileDownload";
import { MaterialReactTable } from "material-react-table";
import { MaterialReactTableConfig } from "components/common/table/TableConfig";
import { readContractItemsImportSheets } from "../../utils/importContractItemsExcel";
import contractItemsApi from "../../services/contractItems.service";
import { AppDialog } from "components/common/dialogs";
import { AppButton } from "components/common/buttons";
import { AppAutocomplete, AppSelect } from "components/common/inputs";
import { getContractImportConfiguration, getContractCommunes } from "../../services/contractItemStructure.service";
import validateContractScopeSheets, { getExpectedContractScopes, getOccupiedContractScopes, requiresContractCommunes } from "../../utils/validateContractScopeSheets";

const ContractItemsImportDialog = ({
  open,
  onClose,
  contractId,
  onImportSuccess,
  onSnackbar,
  existingItems = [],
}) => {
  const [file, setFile] = useState(null);
  const [configuration, setConfiguration] = useState(null);
  const [communes, setCommunes] = useState([]);
  const [sectionId, setSectionId] = useState("");
  const [selectedCommuneIds, setSelectedCommuneIds] = useState([]);
  const [loadingScopes, setLoadingScopes] = useState(false);
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadingScopes(true);
    setConfiguration(null);
    setFile(null); setCommunes([]); setSectionId(""); setSelectedCommuneIds([]); setValidationResult(null); setGeneralError("");
    getContractImportConfiguration(contractId).then(async (config) => {
      const rows = config.provinceId && config.sections.some(requiresContractCommunes) ? await getContractCommunes(config.provinceId) : [];
      if (active) {
        setConfiguration(config); setCommunes(rows);
        setSectionId(config.sections.find((section) => section.isActive)?.contractSectionId || "");
      }
    }).catch(() => { if (active) setGeneralError("Không thể tải scope của hợp đồng. Hãy đóng và mở lại để thử lại."); })
      .finally(() => { if (active) setLoadingScopes(false); });
    return () => { active = false; };
  }, [open, contractId]);
  const sections = useMemo(() => (configuration?.sections || []).filter((section) => section.isActive), [configuration]);
  const selectedSection = sections.find((section) => section.contractSectionId === sectionId);
  const needsCommunes = requiresContractCommunes(selectedSection);
  const occupiedScopes = useMemo(() => getOccupiedContractScopes(existingItems), [existingItems]);
  const availableCommunes = useMemo(() => communes.filter((commune) => {
    const scope = selectedSection?.scopes?.find((entry) => entry.scopeType === "COMMUNE" && entry.communeId === commune.communeId);
    return !scope || (scope.isEditable && !occupiedScopes.has(scope.contractItemScopeId));
  }), [communes, selectedSection, occupiedScopes]);
  const expectedScopes = useMemo(() => getExpectedContractScopes({ section: selectedSection, communes: availableCommunes, selectedCommuneIds, occupiedScopes }),
    [selectedSection, availableCommunes, selectedCommuneIds, occupiedScopes]);
  const scopesReady = !!configuration && (!configuration.sections.length || (!!selectedSection && expectedScopes.length > 0 &&
    (!needsCommunes || (!!configuration.provinceId && selectedCommuneIds.length > 0))));
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [generalError, setGeneralError] = useState("");
  const tableConfig = useMemo(() => MaterialReactTableConfig(), []);
  const templateFileUrl = `${process.env.PUBLIC_URL || ""}/mau-import/hang_muc_hop_dong.xlsx`;

  const canImport = useMemo(
    () =>
      validationResult?.isValid &&
      validationResult?.items?.length > 0 &&
      !importing && !checking && !loadingScopes && scopesReady,
    [importing, checking, loadingScopes, scopesReady, validationResult],
  );

  const errorColumns = useMemo(
    () => [
      {
        accessorKey: "sheetName",
        header: "Sheet",
        size: 140,
      },
      {
        accessorKey: "rowNumber",
        header: "Dòng",
        size: 70,
      },
      {
        accessorKey: "importCode",
        header: "Mã hạng mục",
        size: 100,
      },
      {
        accessorKey: "column",
        header: "Cột",
        size: 140,
      },
      {
        accessorKey: "value",
        header: "Giá trị hiện tại",
        size: 180,
        Cell: ({ cell }) => cell.getValue()?.toString?.() || "",
      },
      {
        accessorKey: "message",
        header: "Mô tả lỗi",
        size: 360,
      },
    ],
    [],
  );

  const errorRows = useMemo(
    () =>
      (validationResult?.errors || []).map((error, index) => ({
        id: `${error.rowNumber || "row"}-${index}`,
        ...error,
      })),
    [validationResult?.errors],
  );

  const errorTableConfig = useMemo(
    () => ({
      ...tableConfig,
      columns: errorColumns,
      data: errorRows,
      enableRowActions: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      enableGlobalFilter: false,
      enableFilters: false,
      enableHiding: false,
      enableTopToolbar: false,
      enableBottomToolbar: errorRows.length > 10,
      enablePagination: errorRows.length > 10,
      enableSorting: true,
      getRowId: (row) => row.id,
      initialState: {
        ...tableConfig.initialState,
        density: "compact",
        pagination: { pageIndex: 0, pageSize: 10 },
      },
      muiTablePaperProps: {
        ...tableConfig.muiTablePaperProps,
        sx: {
          ...tableConfig.muiTablePaperProps?.sx,
          height: "auto",
          maxHeight: 420,
          borderRadius: 1,
        },
      },
      muiTableContainerProps: {
        ...tableConfig.muiTableContainerProps,
        sx: {
          ...tableConfig.muiTableContainerProps?.sx,
          height: "auto",
          maxHeight: 420,
        },
      },
      muiTableBodyCellProps: {
        ...tableConfig.muiTableBodyCellProps,
        sx: {
          ...tableConfig.muiTableBodyCellProps?.sx,
          textAlign: "left",
          whiteSpace: "normal",
          wordBreak: "break-word",
        },
      },
    }),
    [errorColumns, errorRows, tableConfig],
  );

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    event.target.value = "";
    if (!selectedFile) return;
    setFile(selectedFile);
    setValidationResult(null);
    setGeneralError("");
  };
  const changeSection = (value) => {
    setSectionId(Number(value));
    setSelectedCommuneIds([]);
    setValidationResult(null);
    setGeneralError("");
  };
  const changeCommunes = (values) => {
    setSelectedCommuneIds(values.map(Number));
    setValidationResult(null);
    setGeneralError("");
  };

  const handleCheck = async () => {
    if (checking || importing || loadingScopes || !scopesReady) return;
    if (!file) {
      setGeneralError("Vui lòng chọn file Excel trước khi kiểm tra");
      return;
    }

    setChecking(true);
    setGeneralError("");
    setValidationResult(null);

    try {
      if (!configuration) throw new Error("Chưa tải được scope hợp đồng.");
      const sheets = await readContractItemsImportSheets(file);
      const units = await contractItemsApi.getUnitsInContract();
      const unitNames = (units || []).map((unit) => unit.name || unit.Name).filter(Boolean);
      const result = validateContractScopeSheets({ sheets, configuration, section: selectedSection, expectedScopes, unitOptions: unitNames.length ? unitNames : undefined });
      setValidationResult(result);
      if (result.isValid) {
        onSnackbar?.(
          "File hợp lệ. STT trong file chỉ dùng để xác định cấu trúc, hệ thống sẽ tự đánh lại STT khi import.",
          "success",
        );
      }
    } catch (error) {
      console.error("Error checking contract item import file:", error);
      setGeneralError(error.message || "Không thể đọc file Excel");
    } finally {
      setChecking(false);
    }
  };

  const handleImport = async () => {
    if (!canImport) return;

    setImporting(true);
    setGeneralError("");

    try {
      const response = await contractItemsApi.importItems(
        contractId,
        validationResult.items,
        null,
        validationResult.sheets || [],
      );
      if (response.success || response.Success) {
        onSnackbar?.("Import Excel hạng mục hợp đồng thành công", "success");
        onImportSuccess?.();
        onClose?.();
      } else {
        setGeneralError(
          response.message || response.Message || "Import không thành công",
        );
      }
    } catch (error) {
      console.error("Error importing contract items:", error);
      setGeneralError(
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        "Lỗi khi import Excel hạng mục hợp đồng",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (checking || importing) return;
    setFile(null);
    setValidationResult(null);
    setGeneralError("");
    onClose?.();
  };

  return (
    <AppDialog open={open} onClose={handleClose} size="lg" loading={checking || importing || loadingScopes}
      title="Import Excel hạng mục hợp đồng"
      subtitle="Chọn file, kiểm tra từng sheet theo scope trước khi import."
      contentSx={{ backgroundColor: "#fbfdff" }}
      actions={<>
        <AppButton variant="outlined" color="inherit" onClick={handleClose} disabled={checking || importing || loadingScopes}>Hủy</AppButton>
        <AppButton variant="contained" color="primary" onClick={handleImport} disabled={!canImport}>{importing ? "Đang import..." : "Import"}</AppButton>
      </>}>
      <Box>
        {loadingScopes && <LinearProgress sx={{ mb: 2 }} />}
        {configuration?.sections.length > 0 && <Box sx={{ display: "grid", gap: 1.5, mb: 2 }}>
          <AppSelect label="Loại hạng mục" required value={sectionId} options={sections.map((section) => ({ value: section.contractSectionId, label: section.sectionName }))}
            disabled={checking || importing || loadingScopes} onChange={changeSection} />
          {needsCommunes && <>
            {!configuration.provinceId && <Alert severity="warning">Bắt buộc chọn tỉnh/thành phố trong thông tin hợp đồng trước khi chọn phường/xã.</Alert>}
            <AppAutocomplete label="Phường/xã" multiple required value={selectedCommuneIds}
              disabled={!configuration.provinceId || checking || importing || loadingScopes}
              options={availableCommunes.map((commune) => ({ value: commune.communeId, label: commune.communeName }))}
              onChange={changeCommunes} />
            {configuration.provinceId && selectedCommuneIds.length === 0 && <Alert severity="warning">Bắt buộc chọn ít nhất một phường/xã để import hạng mục.</Alert>}
          </>}
        </Box>}
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Danh sách các tỉnh-xã/phân cấp (tên sheet): </strong>
          {expectedScopes.map((scope) => scope.sheetName).join(", ") || (configuration?.sections.length === 0 ? "Một sheet hạng mục" : "Chưa có scope trống được chọn")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 1,
              p: 1.5,
              backgroundColor: "#f8fafc",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontFamily: "Inter, sans-serif", mb: 0.5 }}
            >
              1. Tải file mẫu
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Dùng cấu trúc file mẫu và đặt tên từng sheet hạng mục theo danh sách bên trên.
            </Typography>
            <AppButton
              component="a"
              href={templateFileUrl}
              download="mau_import_hang_muc_hop_dong.xlsx"
              variant="outlined"
              startIcon={<FileDownload />}
              sx={{
                textTransform: "none",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Tải file mẫu
            </AppButton>
          </Box>

          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 1,
              p: 1.5,
              backgroundColor: "#ffffff",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontFamily: "Inter, sans-serif", mb: 1 }}
            >
              2. Chọn file và kiểm tra
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <AppButton
                component="label"
                variant="outlined"
                startIcon={<UploadFile />}
                disabled={checking || importing || loadingScopes}
                sx={{
                  textTransform: "none",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Chọn file Excel
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls"
                  disabled={checking || importing || loadingScopes}
                  onChange={handleFileChange}
                />
              </AppButton>
              <AppButton
                variant="contained"
                onClick={handleCheck}
                disabled={!file || checking || importing || loadingScopes || !scopesReady}
                sx={{
                  textTransform: "none",
                  fontFamily: "Inter, sans-serif",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Kiểm tra dữ liệu
              </AppButton>
            </Box>
            <Typography
              variant="body2"
              color={file ? "text.primary" : "text.secondary"}
              sx={{
                mt: 1,
                fontFamily: "Inter, sans-serif",
                wordBreak: "break-word",
              }}
            >
              {file ? file.name : "Chưa chọn file"}
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Mã hạng mục chỉ dùng để xác định cấp cha/con trong file. Khi lưu vào hệ
          thống, dữ liệu chính thức sẽ được hệ thống tự đánh lại ở cột STT.
        </Alert>

        {(checking || importing) && <LinearProgress sx={{ mb: 2 }} />}

        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}

        {validationResult?.isValid && (
          <Alert severity="success" sx={{ mb: 2 }}>
            File hợp lệ. STT trong file chỉ dùng để xác định cấu trúc, hệ thống
            sẽ tự đánh lại STT khi import. Tổng số hạng mục:{" "}
            {validationResult.items.length}
          </Alert>
        )}

        {validationResult && !validationResult.isValid && (
          <Alert severity="error" sx={{ mb: 2 }}>
            File có {validationResult.errors.length} lỗi. Vui lòng sửa file rồi
            kiểm tra lại.
          </Alert>
        )}

        {validationResult?.errors?.length > 0 && (
          <MaterialReactTable {...errorTableConfig} />
        )}
      </Box>
    </AppDialog>
  );
};

export default ContractItemsImportDialog;
